import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProviderInvoiceStateEnum } from 'src/app/enum/provider-invoice-state-enum';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderInvoiceRetentionModel } from 'src/app/models/shopping/provider-invoice-retention.model';
import { ProviderInvoiceModel } from 'src/app/models/shopping/provider-invoice.model';
import { ProviderInvoiceRetentionTableModel } from 'src/app/models/tables/shopping/provider-invoice-retention-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ProviderInvoiceRetentionService } from 'src/app/services/shopping/provider-invoice-retention.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-list-provider-invoice-retention',
  templateUrl: './list-provider-invoice-retention.component.html',
  styleUrls: ['./list-provider-invoice-retention.component.scss'],
  standalone: false
})
export class ListProviderInvoiceRetentionComponent implements OnInit {

  public readonly config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly providerInvoiceRetentionService = inject(ProviderInvoiceRetentionService);
  private readonly ref = inject(DynamicDialogRef);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  data: PagedResponseModel<ProviderInvoiceRetentionTableModel> = new PagedResponseModel<ProviderInvoiceRetentionTableModel>();
  disabledActions: boolean = false;
  formProviderInvoiceRetention: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  limit: number = 10;
  listTaxes: any[] = [];
  page: number = 1;
  permissionUpdatable: boolean = false;
  providerInvoiceModel: ProviderInvoiceModel = new ProviderInvoiceModel();
  providerInvoiceRetentionModel: ProviderInvoiceRetentionModel = new ProviderInvoiceRetentionModel();
  suscription: Subscription;
  tableActions: TableAction[] = [];
  taxType: string = '';
  totalValue: number = 0;
  updateProviderInvoice: boolean = false;

  async ngOnInit(): Promise<void> {
    this.validateForm(new ProviderInvoiceRetentionModel());
    await this.getColumns();
    await this.getListTaxes();

    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.providerInvoiceModel = inputs.providerInvoiceModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.disabledActions = inputs.disabledForm;
    });

    await this.getProviderInvoiceRetentionsByProviderInvoiceIdWithPagination();
    this.tableActions = this.getDefaultActions();

    if (this.disabledActions) {
      this.formProviderInvoiceRetention.disable();
    } else {
      this.formProviderInvoiceRetention.enable();
      this.formControlStateService.setControlState(this.formProviderInvoiceRetention, ['taxRate'], false);
    }
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async cancel() {
    this.providerInvoiceRetentionModel = new ProviderInvoiceRetentionModel();
    this.validateForm(this.providerInvoiceRetentionModel);
    this.taxType = '';
    this.totalValue = 0;
  }

  changeBaseValue(baseValue: number) {
    const taxRate = this.formProviderInvoiceRetention.get('taxRate')?.value ?? 0;
    const taxId = this.formProviderInvoiceRetention.get('taxId')?.value ?? 0;
    const taxType = this.listTaxes.find(x => x.value === taxId)?.taxType ?? '';
    this.totalValue = taxType === 'ICA' ? (baseValue) * (taxRate) / 1000 : (baseValue) * (taxRate) / 100;
  }

  changeTax(taxId: number) {
    //Buscar el impuesto y traerlo si existe para evitar duplicados
    //tener en cuenta los que se importan automaticamente desde el detalle
    const tax = this.listTaxes.find(x => x.value === taxId);
    const taxType = this.listTaxes.find(x => x.value === taxId)?.taxType ?? '';
    const baseValue = this.providerInvoiceRetentionModel.providerInvoiceRetentionId === 0 ?
      this.providerInvoiceModel?.subtotalValue ?? 0 : this.providerInvoiceRetentionModel.baseValue;
    this.taxType = taxType;
    this.formProviderInvoiceRetention.get('taxRate')?.patchValue(tax?.rate ?? 0);
    this.formProviderInvoiceRetention.get('baseValue')?.patchValue(baseValue);
    this.totalValue = this.taxType === 'ICA' ? (this.formProviderInvoiceRetention.get('baseValue')?.value ?? 0) * (this.formProviderInvoiceRetention.get('taxRate')?.value ?? 0) / 1000 :
      (this.formProviderInvoiceRetention.get('baseValue')?.value ?? 0) * (this.formProviderInvoiceRetention.get('taxRate')?.value ?? 0) / 100;
  }

  close() {
    const data: ModalObjectType = {};
    data.params = { updateProviderInvoice: this.updateProviderInvoice };
    this.ref.close(data);
  }

  async deleteDetail(selectedIds: number[]) {
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar el detalle seleccionado?`);
    if (accepted) {
      try {
        await firstValueFrom(this.providerInvoiceRetentionService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle fue eliminado exitosamente.');
        this.getProviderInvoiceRetentionsByProviderInvoiceIdWithPagination();
      } catch (error) {
        console.error('Error al eliminar el detalle de la retención.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.providerInvoiceRetentionService.getColumnsForProviderInvoiceRetentionAsync());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.providerInvoiceModel.state == ProviderInvoiceStateEnum.open) {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    } else {
      return [];
    }
  }

  async getListTaxes() {
    try {
      this.listTaxes = await firstValueFrom(this.providerInvoiceRetentionService.getAllTaxesForSelectAsync());
    } catch (error) {
      console.error('Error al obtener la lista de impuestos.', error);
    }
  }

  async getProviderInvoiceRetentionsByProviderInvoiceIdWithPagination() {
    try {
      this.data = await firstValueFrom(
        this.providerInvoiceRetentionService.getProviderInvoiceRetentionsByProviderInvoiceIdWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.providerInvoiceModel.providerInvoiceId
        ));
      this.clearSelectionTrigger++;
    } catch (error) {
      console.error('Error al buscar las retenciones.', error);
    }
  }

  async onAction(event: { action: string, item?: ProviderInvoiceRetentionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      try {
        if (!event.item.swManual) {
          this.toastHandlerService.showToast('warning', `No se puede editar una retención automatica.`);
          return;
        }
        this.providerInvoiceRetentionModel = await firstValueFrom(
          this.providerInvoiceRetentionService.getProviderInvoiceRetentionById(event.item.id)
        );
        this.validateForm(this.providerInvoiceRetentionModel);
        this.changeTax(this.providerInvoiceRetentionModel.taxId);
      } catch (error) {
        console.error('Error al editar la retención:', error);
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteDetail(event.selectedItems);
      this.cancel();
      this.updateProviderInvoice = true;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getProviderInvoiceRetentionsByProviderInvoiceIdWithPagination();
  }

  setForm(): ProviderInvoiceRetentionModel {
    this.providerInvoiceRetentionModel.providerInvoiceId = this.providerInvoiceModel.providerInvoiceId;
    this.providerInvoiceRetentionModel.taxId = this.formProviderInvoiceRetention.get("taxId")?.value;
    this.providerInvoiceRetentionModel.baseValue = this.formProviderInvoiceRetention.get("baseValue")?.value;
    this.providerInvoiceRetentionModel.taxRate = this.formProviderInvoiceRetention.get("taxRate")?.value;
    this.providerInvoiceRetentionModel.swManual = true;
    return this.providerInvoiceRetentionModel;
  }

  async send() {
    Object.keys(this.formProviderInvoiceRetention.controls).forEach((key) => {
      const control = this.formProviderInvoiceRetention.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formProviderInvoiceRetention.valid) {
      const modelProviderInvoiceRetention = this.setForm();
      const isNew = modelProviderInvoiceRetention.providerInvoiceRetentionId === 0;
      try {
        await firstValueFrom(isNew
          ? this.providerInvoiceRetentionService.createProviderInvoiceRetention(modelProviderInvoiceRetention)
          : this.providerInvoiceRetentionService.updateProviderInvoiceRetention(modelProviderInvoiceRetention));
        this.getProviderInvoiceRetentionsByProviderInvoiceIdWithPagination();
        this.updateProviderInvoice = true;
        this.toastHandlerService.showToast('success', `La retención se ${isNew ? 'creó' : 'modificó'} exitosamente.`);
        this.cancel();
      } catch (error) {
        console.error('Error al guardar la retención', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      taxId: {
        required: 'Debe seleccionar un impuesto'
      },
      baseValue: {
        required: 'Debe ingresar la base',
        min: 'Debe ingresar una base mayor a 0'
      },
      taxRate: {
        required: 'Debe ingresar el porcentaje',
        min: 'Debe ingresar un porcentaje igual o mayor a 0',
        max: 'El porcentaje no puede ser superior a 100'
      }
    };
    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formProviderInvoiceRetention.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(providerInvoiceRetention: ProviderInvoiceRetentionModel) {
    this.formProviderInvoiceRetention = this.formBuilder.group({
      taxId: [providerInvoiceRetention.taxId || null, [Validators.required]],
      baseValue: [providerInvoiceRetention.baseValue, [Validators.required, Validators.min(1)]],
      taxRate: [providerInvoiceRetention.taxRate, [Validators.required, Validators.min(0), Validators.max(100)]],
    });

    this.formControlStateService.setControlState(this.formProviderInvoiceRetention, ['taxRate'], false);
  }

}
